import React, { useState, useRef, useEffect } from 'react';
import scanner from './ScannerForm';
const ScannerCamera = ({ onCancel, onCapture }) => {
  const [errorMsg, setErrorMsg] = useState('');
  const [isReady, setIsReady] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Initialize camera on component mount
  useEffect(() => {
    console.log('ScannerCamera component mounted');
    
    if (isMobile) {
      console.log('Mobile device detected, using file input');
      setTimeout(() => {
        if (fileInputRef.current) fileInputRef.current.click();
      }, 100);
    } else {
      console.log('Desktop device detected, initializing camera');
      startCamera();
    }
    
    // Critical cleanup function that MUST run when component unmounts
    return () => {
      console.log('ScannerCamera component unmounting - stopping camera');
      stopAllCameras();
    };
  }, []);

  // Force stop ALL camera streams in the browser
  const stopAllCameras = () => {
    // 1. Stop our known stream first
    if (streamRef.current) {
      console.log('Stopping known camera stream');
      const tracks = streamRef.current.getTracks();
      tracks.forEach(track => {
        console.log(`Stopping track: ${track.kind}`);
        track.stop();
      });
      streamRef.current = null;
    }
    
    // 2. Clear video source
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    // 3. Reset UI state
    setIsReady(false);
    
    // 4. Make this change visible to browser garbage collection immediately
    setTimeout(() => {
      console.log('Final camera cleanup completed');
    }, 0);
  };

  // Start camera
  const startCamera = async () => {
    try {
      console.log('Requesting camera access');
      
      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false
      });
      
      console.log('Camera access granted');
      
      // Store stream reference for later cleanup
      streamRef.current = stream;
      
      // Connect stream to video element
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // Wait for video to be ready
        videoRef.current.onloadedmetadata = () => {
          console.log('Video metadata loaded, camera ready');
          setIsReady(true);
        };
      }
    } catch (err) {
      console.error('Camera error:', err);
      setErrorMsg(`Could not access camera: ${err.message}`);
    }
  };

  // Handle cancel button
  const handleCancel = () => {
    console.log('Cancel button clicked');
    
    // Stop camera first
    stopAllCameras();
    
    // Then notify parent
    onCancel();
  };

  // Capture photo
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current || !streamRef.current) {
      console.error('Cannot capture photo - video not ready');
      return;
    }
    
    console.log('Capturing photo');
    
    // Get video dimensions
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw video frame to canvas
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    // Get image data
    const imageData = canvas.toDataURL('image/jpeg', 0.9);
    
    // Stop camera first
    stopAllCameras();
    
    // Then send image to parent
    onCapture(imageData);
  };

  // Handle file selection
  const handleFileChange = (e) => {
    if (!e.target.files || !e.target.files[0]) return;
    
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = (event) => {
      onCapture(event.target.result);
    };
    reader.onerror = () => {
      setErrorMsg('Error reading the image file.');
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex flex-col items-center justify-center p-4">
      <h2 className="text-xl font-semibold mb-4">Take a Photo</h2>
      
      {errorMsg && (
        <div className="mb-4 bg-red-100 p-3 rounded text-red-700">
          {errorMsg}
        </div>
      )}
      
      {!isMobile && (
        <div className="w-full max-w-lg mb-4">
          <div className="relative">
            <video 
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full border rounded bg-gray-100"
              style={{ minHeight: '300px' }}
            />
            
            {!isReady && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-70">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  <p className="mt-2 text-gray-700">Loading camera...</p>
                </div>
              </div>
            )}
          </div>
          
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          
          <button
            onClick={capturePhoto}
            className="w-full mt-4 py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700"
            disabled={!isReady}
          >
            {isReady ? 'Take Photo' : 'Loading Camera...'}
          </button>
        </div>
      )}
      
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        capture={isMobile ? "environment" : undefined}
        onChange={handleFileChange}
        className="hidden"
      />
      
      <div className="mt-4">
        {!isMobile && (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="mr-4 py-2 px-4 bg-green-600 text-white rounded hover:bg-green-700"
          >
            Upload Photo
          </button>
        )}
        
        <button
          onClick={handleCancel}
          className="py-2 px-4 bg-gray-500 text-white rounded hover:bg-gray-600"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default ScannerCamera;